import {
  FailedToAttachError,
  FailedToBindError,
  FailedToGetBindingError,
  FailedToListBindingsError,
  FailedToListProvidersError,
  FailedToSetBindingError,
  FailedToUnbindError,
  FailedToDetachError,
  FailedToFindBindingCandidatesError,
} from '@wix/editor-platform-feature-store';
import {
  Provider,
  AttachStateFilter,
  ListProvidersOptions,
  UnbindAll,
  BindingProperties,
  BindingDetailsMap,
  ListBindingsOptions,
  BindingCandidate,
  findBindingCandidatesOptions,
} from '@wix/public-editor-platform-interfaces';

import { VirtualElementAPIType } from '../providers/VirtualElement';
import { AbstractManifestAPI } from '../AbstractManifestAPI';
import { ManifestQueryAPI } from '../../handlers';
import type { BindingTargetDetails } from '@wix/editor-platform-feature-store';

export class ManifestProviderQuery extends AbstractManifestAPI<
  ManifestQueryAPI,
  | VirtualElementAPIType.ManifestQuery
  | VirtualElementAPIType.ManifestQueryReadonly
> {
  listProviders(
    options?: ListProvidersOptions,
  ): FailedToListProvidersError | Provider[] {
    const createInnerIncludeAttachState = (
      includeAttachState: AttachStateFilter | undefined,
    ) => {
      if (!includeAttachState) {
        return undefined;
      }

      if (includeAttachState === true) {
        return { target: { editorPointer: this.node.getEditorPointer() } };
      }
      return {
        target: { editorPointer: this.node.getEditorPointer() },
        filterAttachState: includeAttachState.filterAttachedState,
      };
    };

    const includeAttachState = createInnerIncludeAttachState(
      options?.includeAttachState,
    );

    return this.api.providers.list({
      ...options,
      includeAttachState,
    });
  }

  async attachProvider(
    providerIdentifier: string,
    data?: Object,
  ): Promise<FailedToAttachError | void> {
    return this.api.providers.attach(
      this.node.getEditorPointer(),
      providerIdentifier,
      data,
    );
  }

  async detachProvider(
    providerIdentifier: string,
  ): Promise<FailedToDetachError | void> {
    return this.api.providers.detach(
      this.node.getEditorPointer(),
      providerIdentifier,
    );
  }

  async bind(bindings: BindingProperties[]): Promise<void | FailedToBindError> {
    return this.api.bindings.bind(
      { editorPointer: this.node.getEditorPointer() },
      bindings,
    );
  }

  async setBinding(
    bindings: BindingDetailsMap,
  ): Promise<void | FailedToSetBindingError> {
    return this.api.bindings.setBinding(
      { editorPointer: this.node.getEditorPointer() },
      bindings,
    );
  }

  async unbind(
    props: string[] | UnbindAll,
  ): Promise<void | FailedToUnbindError> {
    return this.api.bindings.unbind(
      { editorPointer: this.node.getEditorPointer() },
      props,
    );
  }

  listBindings(
    options?: ListBindingsOptions,
  ): FailedToListBindingsError | BindingProperties[] {
    return this.api.bindings.listBindings(
      { editorPointer: this.node.getEditorPointer() },
      options,
    );
  }

  getBinding(
    options?: ListBindingsOptions,
  ): FailedToGetBindingError | BindingDetailsMap {
    return this.api.bindings.getBinding(
      { editorPointer: this.node.getEditorPointer() },
      options,
    );
  }

  findBindingCandidates(
    bindingTargetDetails: BindingTargetDetails,
    options?: findBindingCandidatesOptions,
  ): BindingCandidate[] | FailedToFindBindingCandidatesError {
    return this.api.bindings.findBindingCandidates({
      target: { editorPointer: this.node.getEditorPointer() },
      bindingTargetDetails,
      ...options,
    });
  }
}
